<template>
  <div class="min-h-screen bg-slate-50/50 pb-20 font-sans">
    
    <!-- Hero / Top Section -->
    <div class="relative overflow-hidden bg-white border-b border-gray-100 py-16 px-4 sm:px-6 lg:px-8 text-center">
      <!-- Background subtle gradients -->
      <div class="absolute inset-0 opacity-30 pointer-events-none">
        <div class="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-gradient-to-b from-indigo-200 to-transparent rounded-full filter blur-3xl"></div>
      </div>

      <div class="relative max-w-4xl mx-auto">
        <!-- Logo Badge -->
        <div class="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 border border-indigo-150 text-indigo-700 text-xs font-bold rounded-full mb-6">
          <div class="w-2 h-2 bg-indigo-600 rounded-full animate-pulse"></div>
          TrustSERP / SERPProof
        </div>

        <h1 class="text-4xl sm:text-5xl font-black text-gray-900 tracking-tight leading-none">
          The database of <span class="text-indigo-600 underline decoration-indigo-200 decoration-wavy">verified</span> SEO leaders.
        </h1>
        <p class="mt-4 text-base sm:text-lg text-gray-500 max-w-2xl mx-auto font-medium">
          Real organic traffic metrics straight from Google Search Console, API-to-database. No screenshots, no inspection fakes.
        </p>

        <div class="mt-8">
          <a href="/dashboard" class="inline-flex items-center gap-2 px-6 py-3.5 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] transition-all text-white font-bold rounded-xl shadow-lg shadow-indigo-100 text-sm sm:text-base">
            Verify my search traffic <i class="fa-solid fa-arrow-right"></i>
          </a>
        </div>

        <!-- Features list -->
        <div class="mt-8 flex flex-wrap justify-center items-center gap-x-8 gap-y-3 text-xs sm:text-sm font-semibold text-gray-500">
          <div class="flex items-center gap-1.5">
            <i class="fa-solid fa-circle-check text-indigo-600"></i> 100% verified GSC Data
          </div>
          <div class="flex items-center gap-1.5">
            <i class="fa-solid fa-circle-check text-indigo-600"></i> Updated in real-time
          </div>
          <div class="flex items-center gap-1.5">
            <i class="fa-solid fa-circle-check text-indigo-600"></i> No screenshots or PDF uploads
          </div>
        </div>
      </div>
    </div>

    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
      
      <!-- Featured Leaders Cards -->
      <div v-if="featured.length > 0" class="mb-14">
        <div class="flex justify-between items-center mb-6">
          <h3 class="text-xs uppercase tracking-wider font-extrabold text-gray-400">Featured SEO Leaders</h3>
          <span class="text-xs text-indigo-600 font-bold">Top performance this month</span>
        </div>
        
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <div v-for="(leader, idx) in featured.slice(0, 4)" :key="leader.id" class="bg-white rounded-2xl border border-gray-150 p-5 shadow-sm hover:shadow-md transition-all flex flex-col justify-between">
            <div class="flex items-start justify-between gap-2">
              <div class="flex items-center gap-3">
                <!-- Initials Avatar with custom gradient -->
                <div :class="`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-base text-white ${getGradientClass(idx)}`">
                  {{ getInitials(leader.user?.name) }}
                </div>
                <div class="truncate">
                  <h4 class="font-bold text-gray-900 truncate leading-snug" :title="leader.user?.name">{{ leader.user?.name }}</h4>
                  <span class="inline-flex text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full mt-0.5">
                    {{ leader.user?.public_profile?.category || 'SaaS' }}
                  </span>
                </div>
              </div>
              <div class="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center text-blue-600" title="GSC Verified">
                <i class="fa-solid fa-circle-check text-xs"></i>
              </div>
            </div>

            <!-- Stats section -->
            <div class="mt-5 pt-4 border-t border-gray-100 grid grid-cols-2 gap-2">
              <div>
                <span class="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider block">30D Clicks</span>
                <span class="text-lg font-black text-gray-900 mt-0.5 block">{{ formatNumber(leader.clicks) }}</span>
              </div>
              <div>
                <span class="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider block">Impressions</span>
                <span class="text-lg font-black text-indigo-600 mt-0.5 block">{{ formatNumber(leader.impressions) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Ranking Table and Controls -->
      <div class="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        
        <!-- Controls Header -->
        <div class="p-6 border-b border-gray-200 bg-gray-50/50">
          <div class="flex flex-col gap-4">
            
            <!-- Title -->
            <div>
              <h2 class="text-xl font-bold text-gray-900">Verified SEO Leaders</h2>
              <p class="text-xs text-gray-500 mt-0.5">Filter organic GSC traffic data by niche, user type, and timeframe.</p>
            </div>

            <!-- Filter Controls Row -->
            <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pt-2">
              
              <!-- Sponsors vs Top Sites Toggle -->
              <div class="flex items-center bg-gray-150 p-1 rounded-xl self-start">
                <button 
                  @click="setType('top_sites')"
                  :class="`px-4 py-2 rounded-lg text-xs font-bold transition-all ${filters.type === 'top_sites' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`"
                >
                  Top Sites
                </button>
                <button 
                  @click="setType('sponsors')"
                  :class="`px-4 py-2 rounded-lg text-xs font-bold transition-all ${filters.type === 'sponsors' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`"
                >
                  Sponsors
                </button>
              </div>

              <!-- Timeframe Selector -->
              <div class="flex items-center bg-gray-150 p-1 rounded-xl self-start">
                <button 
                  @click="setDateRange('last_30_days')"
                  :class="`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${filters.date_range === 'last_30_days' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`"
                >
                  30 Days
                </button>
                <button 
                  @click="setDateRange('last_3_months')"
                  :class="`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${filters.date_range === 'last_3_months' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`"
                >
                  3 Months
                </button>
                <button 
                  @click="setDateRange('last_12_months')"
                  :class="`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${filters.date_range === 'last_12_months' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`"
                >
                  12 Months
                </button>
              </div>

              <!-- Search Box -->
              <div class="relative w-full lg:w-64">
                <i class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                <input 
                  type="text" 
                  v-model="filters.search"
                  @input="onSearchInput"
                  placeholder="Search websites or creators..."
                  class="w-full pl-9 pr-4 py-2 bg-white border border-gray-350 rounded-xl text-xs font-semibold focus:outline-none focus:border-indigo-500 transition-colors"
                />
              </div>

            </div>

            <!-- Categories / Niches Chips -->
            <div class="flex flex-wrap gap-1.5 pt-1">
              <button 
                v-for="cat in categories" 
                :key="cat"
                @click="setCategory(cat)"
                :class="`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${filters.category === cat ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm shadow-indigo-100' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`"
              >
                {{ cat }}
              </button>
            </div>

          </div>
        </div>

        <!-- Table Display -->
        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-gray-50 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider border-b border-gray-150">
                <th class="py-4 px-6 w-16 text-center">#</th>
                <th class="py-4 px-6">Website / Leader</th>
                <th class="py-4 px-6 text-right">Clicks</th>
                <th class="py-4 px-6 text-right">Impressions</th>
                <th class="py-4 px-6 text-right">Avg CTR</th>
                <th class="py-4 px-6 text-right">Avg Pos</th>
              </tr>
            </thead>
            <tbody v-if="loading" class="divide-y divide-gray-100">
              <tr v-for="i in 5" :key="i" class="animate-pulse">
                <td class="py-5 px-6"><div class="h-4 bg-gray-200 rounded w-6 mx-auto"></div></td>
                <td class="py-5 px-6">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 bg-gray-200 rounded-lg"></div>
                    <div class="space-y-2">
                      <div class="h-4 bg-gray-200 rounded w-32"></div>
                      <div class="h-3 bg-gray-200 rounded w-20"></div>
                    </div>
                  </div>
                </td>
                <td class="py-5 px-6"><div class="h-4 bg-gray-200 rounded w-16 ml-auto"></div></td>
                <td class="py-5 px-6"><div class="h-4 bg-gray-200 rounded w-20 ml-auto"></div></td>
                <td class="py-5 px-6"><div class="h-4 bg-gray-200 rounded w-12 ml-auto"></div></td>
                <td class="py-5 px-6"><div class="h-4 bg-gray-200 rounded w-10 ml-auto"></div></td>
              </tr>
            </tbody>
            <tbody v-else-if="leaders.length === 0" class="divide-y divide-gray-100">
              <tr>
                <td colspan="6" class="py-16 text-center text-gray-400">
                  <div class="text-3xl mb-2"><i class="fa-solid fa-list-check"></i></div>
                  <p class="text-sm font-semibold">No verified properties found</p>
                  <p class="text-xs text-gray-400 mt-1">Try adapting your search or filter options.</p>
                </td>
              </tr>
            </tbody>
            <tbody v-else class="divide-y divide-gray-100">
              <tr v-for="(item, idx) in leaders" :key="item.id" class="hover:bg-gray-50/50 transition-colors group">
                <!-- Rank -->
                <td class="py-4 px-6 text-center">
                  <span v-if="idx === 0" class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-50 border border-amber-200 text-amber-600 font-extrabold text-xs">1</span>
                  <span v-else-if="idx === 1" class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 border border-slate-200 text-slate-600 font-extrabold text-xs">2</span>
                  <span v-else-if="idx === 2" class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-50 border border-orange-200 text-orange-600 font-extrabold text-xs">3</span>
                  <span v-else class="text-xs font-semibold text-gray-400">{{ idx + 1 }}</span>
                </td>

                <!-- Website / Leader -->
                <td class="py-4 px-6">
                  <div class="flex items-center gap-3">
                    <!-- Initials Avatar -->
                    <div :class="`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm text-white ${getGradientClass(idx)}`">
                      {{ getInitials(item.user?.name) }}
                    </div>
                    
                    <div>
                      <div class="flex items-center gap-2">
                        <span class="text-sm font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">
                          {{ item.user?.name }}
                        </span>
                        
                        <!-- Verified check badge -->
                        <div class="text-blue-500" title="GSC API Verified">
                          <i class="fa-solid fa-circle-check text-xs"></i>
                        </div>
                        
                        <!-- Sponsor Tag if applicable -->
                        <span v-if="item.user?.public_profile?.bio?.includes('Sponsor')" class="text-[9px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded font-extrabold uppercase tracking-wide">
                          Sponsor
                        </span>
                      </div>

                      <div class="flex items-center gap-2 mt-0.5">
                        <!-- Category pill -->
                        <span class="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                          {{ item.user?.public_profile?.category || 'SaaS' }}
                        </span>
                        
                        <!-- Domain url or blurred domain -->
                        <span class="text-xs font-mono font-medium text-gray-400">
                          <span v-if="item.property?.privacy_mode === 'public_domain'">
                            {{ cleanUrl(item.property?.site_url) }}
                          </span>
                          <span v-else class="flex items-center gap-1 text-[11px] text-gray-400 italic">
                            <i class="fa-solid fa-eye-slash text-[10px]"></i> Domain Blurred
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </td>

                <!-- Clicks -->
                <td class="py-4 px-6 text-right font-bold text-gray-900 text-sm">
                  {{ formatNumber(item.clicks) }}
                </td>

                <!-- Impressions -->
                <td class="py-4 px-6 text-right font-bold text-indigo-600/90 text-sm">
                  {{ formatNumber(item.impressions) }}
                </td>

                <!-- CTR -->
                <td class="py-4 px-6 text-right font-bold text-emerald-600 text-sm">
                  {{ item.ctr }}%
                </td>

                <!-- Position -->
                <td class="py-4 px-6 text-right font-bold text-amber-600 text-sm">
                  {{ item.avg_position }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>

      <!-- Trust & Info Section -->
      <div class="mt-20 border-t border-gray-200 pt-16">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <!-- Col 1 -->
          <div class="flex items-start gap-4">
            <div class="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 text-xl shrink-0">
              <i class="fa-solid fa-eye-slash"></i>
            </div>
            <div>
              <h4 class="font-bold text-gray-900">Your domain stays hidden</h4>
              <p class="text-xs text-gray-500 mt-1.5 leading-relaxed">
                Choose to hide your domain from public view at any time. We will blur the URL on the leaderboard while still showing your verified traffic metrics and category pill.
              </p>
            </div>
          </div>

          <!-- Col 2 -->
          <div class="flex items-start gap-4">
            <div class="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 text-xl shrink-0">
              <i class="fa-solid fa-shield-halved"></i>
            </div>
            <div>
              <h4 class="font-bold text-gray-900">Read-only integrations</h4>
              <p class="text-xs text-gray-500 mt-1.5 leading-relaxed">
                Our platform requests strict read-only access scopes (`webmasters.readonly`). We never alter or modify any settings in your Google Search Console account.
              </p>
            </div>
          </div>

          <!-- Col 3 -->
          <div class="flex items-start gap-4">
            <div class="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 text-xl shrink-0">
              <i class="fa-solid fa-circle-check"></i>
            </div>
            <div>
              <h4 class="font-bold text-gray-900">Proof, not screenshots</h4>
              <p class="text-xs text-gray-500 mt-1.5 leading-relaxed">
                Screenshots, reports, and PDFs are easily falsified using inspect-element tools. Connecting via official API guarantees your numbers are 100% authentic.
              </p>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'LeaderboardView',
  data() {
    return {
      leaders: [],
      featured: [],
      loading: true,
      categories: ['All', 'SaaS', 'Ecommerce', 'Agency', 'Affiliate', 'Local', 'Other'],
      filters: {
        type: 'top_sites',
        date_range: 'last_30_days',
        category: 'All',
        search: ''
      },
      searchTimeout: null
    };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const response = await axios.get('/api/leaderboard', {
          params: this.filters
        });
        this.leaders = response.data.leaders || [];
        this.featured = response.data.featured || [];
      } catch (err) {
        console.error('Failed fetching leaderboard stats:', err);
      } finally {
        this.loading = false;
      }
    },
    setType(type) {
      if (this.filters.type === type) return;
      this.filters.type = type;
      this.fetchData();
    },
    setDateRange(range) {
      if (this.filters.date_range === range) return;
      this.filters.date_range = range;
      this.fetchData();
    },
    setCategory(cat) {
      if (this.filters.category === cat) return;
      this.filters.category = cat;
      this.fetchData();
    },
    onSearchInput() {
      // Debounce search requests
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchData();
      }, 300);
    },
    formatNumber(num) {
      if (num === null || num === undefined) return '0';
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
      return num.toLocaleString();
    },
    cleanUrl(url) {
      if (!url) return '';
      return url.replace(/^https?:\/\//i, '').replace(/^sc-domain:/i, '').replace(/\/$/, '');
    },
    getInitials(name) {
      if (!name) return 'U';
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    getGradientClass(idx) {
      const gradients = [
        'bg-gradient-to-tr from-indigo-500 to-indigo-600 shadow-indigo-100',
        'bg-gradient-to-tr from-emerald-500 to-emerald-600 shadow-emerald-100',
        'bg-gradient-to-tr from-blue-500 to-blue-600 shadow-blue-100',
        'bg-gradient-to-tr from-amber-500 to-amber-600 shadow-amber-100',
        'bg-gradient-to-tr from-rose-500 to-rose-600 shadow-rose-100',
        'bg-gradient-to-tr from-violet-500 to-violet-600 shadow-violet-100',
      ];
      return gradients[idx % gradients.length];
    }
  }
};
</script>

<style scoped>
/* Glassmorphism subtle overlay */
.glass {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(8px);
}
</style>
